import dayjs from "dayjs";
import { safeParseJson } from "../constants/utils";
import {
  BLACK_FRIDAY_COPY,
  BLACK_FRIDAY_THEME,
  BLACK_FRIDAY_MODAL_SEEN_KEY,
} from "../constants/blackFriday";

export const isBlackFridayCard = (card) =>
  String(card?.theme ?? "")
    .trim()
    .toLowerCase() === BLACK_FRIDAY_THEME;

/**
 * The selected property's live Black Friday card from the v2 feed, or null.
 * The feed already drops a card once its promo is published; expiry is checked
 * here because the read does not filter on expiry_date.
 *
 * The feed must belong to `propertyId`: right after a property switch the hooks
 * still hold the previous hotel's feed until the new one lands, and its card
 * would otherwise flash the strip and pop-up for the wrong hotel.
 */
export const findBlackFridayCard = (v2Data, propertyId, today = dayjs()) => {
  if (propertyId == null || String(v2Data?.propertyId) !== String(propertyId)) {
    return null;
  }
  return (
    (v2Data?.cards || []).find(
      (card) =>
        isBlackFridayCard(card) &&
        (!card.expiryDate || !dayjs(card.expiryDate).isBefore(today, "day"))
    ) ?? null
  );
};

/**
 * Discount and booking window out of the card's promo_object_dr. A flat
 * discount (every night the same) reads as "30%"; anything else has no single
 * number to show and comes back null.
 */
export const getBlackFridayOffer = (card) => {
  const raw = card?.promoObjectDr;
  const promo = typeof raw === "string" ? safeParseJson(raw) : raw;
  const config = (promo?.snapshot || promo)?.promoConfig || {};
  const deltas = Object.values(config.priceConfig?.[0]?.priceDelta || {});
  const flat = deltas.length > 0 && deltas.every((v) => v === deltas[0]);
  const amount = Number(deltas[0]);
  const window = config.restrictionConfig?.bookDates?.[0] || {};
  return {
    discount: flat && Number.isFinite(amount) ? `${amount}%` : null,
    bookingStart: window.startDate || null,
    bookingEnd: window.endDate || null,
  };
};

/**
 * The card's rows as { label, value, isDiscount }: the data team's metrics_json
 * as sent (Figma shows those values verbatim), else the discount and booking
 * window read from the promo.
 */
export const getBlackFridayRows = (card) => {
  const metrics = (card?.metrics || []).filter(
    (metric) => metric?.label && metric?.value
  );
  if (metrics.length) {
    return metrics.map(({ label, value }) => ({
      label,
      value,
      isDiscount: /discount/i.test(label),
    }));
  }
  const { discount, bookingStart, bookingEnd } = getBlackFridayOffer(card);
  const bookingWindow = formatOfferWindow(bookingStart, bookingEnd);
  return [
    discount && {
      label: BLACK_FRIDAY_COPY.cardDiscountLabel,
      value: discount,
      isDiscount: true,
    },
    bookingWindow && {
      label: BLACK_FRIDAY_COPY.cardWindowLabel,
      value: bookingWindow,
      isDiscount: false,
    },
  ].filter(Boolean);
};

/** "2026-11-13" → "Nov 13, 2026" for the strip's publish-by line. */
export const formatOfferDeadline = (date) =>
  date ? dayjs(date).format("MMM D, YYYY") : "";

/**
 * "13 Nov – 3 Dec, 2026" in one year, "13 Nov 2026 – 31 Dec 2027" across two;
 * one side alone when the other is missing.
 */
export const formatOfferWindow = (start, end) => {
  if (!start || !end) {
    const only = start || end;
    return only ? dayjs(only).format("D MMM YYYY") : "";
  }
  const from = dayjs(start);
  const to = dayjs(end);
  return from.year() === to.year()
    ? `${from.format("D MMM")} – ${to.format("D MMM, YYYY")}`
    : `${from.format("D MMM YYYY")} – ${to.format("D MMM YYYY")}`;
};

// One flag per user: once seen on any property, the pop-up stays closed for
// the rest of the session.
const modalSeenKey = (userId) => `${BLACK_FRIDAY_MODAL_SEEN_KEY}:${userId}`;

export const hasSeenBlackFridayModal = (userId) => {
  try {
    return sessionStorage.getItem(modalSeenKey(userId)) === "1";
  } catch {
    return false;
  }
};

export const markBlackFridayModalSeen = (userId) => {
  try {
    sessionStorage.setItem(modalSeenKey(userId), "1");
  } catch {
    // Storage blocked: the pop-up may show again, which is harmless.
  }
};
